import { useState, useEffect, useMemo, useCallback, forwardRef, useRef } from 'react';
import MuiAutocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import SearchIcon from '@mui/icons-material/Search';
import { SearchIconButton } from '@Components/MuiWrappers/Button';
import TextField from '@mui/material/TextField';
import throttle from 'lodash/throttle';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { sandhillsRequest, gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import { gtmEventType } from '@Components/Utilities/SandhillsConstants';
import { trackAutocompleteSearchAction } from '@Components/Utilities/Tracking';
import translator from '@Components/Utilities/Translator';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { setSearchHistoryCookie, getSearchHistoryValues } from '@Components/Utilities/CookieHelper';
import ListSubheader from '@mui/material/ListSubheader';
import Collapse from '@mui/material/Collapse';
import { QuickFindSearchButton } from '@Components/MuiWrappers/Button';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

const quickFindRegex = /^([a-z]{2})(\d{2})([a-z]{2})$/i;

// This autocomplete wrapper is used for the header quick find search, drilldown keyword search, and drilldown city/state/zip field on RY
// Please test all areas listed above when making changes to this component
const Autocomplete = forwardRef((props, ref) => {
    const routes = useRecoilValue(atoms.routes);
    const windowSize = useWindowSize();
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const popperRef = useRef(null);

    const [history, setHistory] = useState();
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setOpen] = useState(false);
    const [options, setOptions] = useState(props.options || []);
    const [optionsVisible, setOptionsVisible] = useState(true);
    const [popperOpacity, setPopperOpacity] = useState(1);
    const [searchType, setSearchType] = useState(props.searchType);
    const [value, setValue] = useState(props.value ?? null);
    const [hideLabel, setHideLabel] = useState(false); 

    const { // props destructuring with defaults
        getOptionLabel = (option) => option.value || option,
        onChange = (e, newValue) => {
            if (props.isQuickFind) {
                setHideLabel(true); 
            }
            if (newValue?.Url) {
                trackingOptions.save(newValue);
                if (props.showHistory) {
                    setSearchHistoryCookie(newValue.Label, newValue.Url);
                }
                window.location.href = newValue.Url;
            } else if (e?.key == 'Enter') {
                return searchOptions.runSearch();
            } else {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
            }
        }
    } = props

    useEffect(() => {
        const h = getSearchHistoryValues();
        setHistory(h);
        // If input is empty and history should be shown, set options to reversed history (most recent first)
        if (props.showHistory && (!inputValue || inputValue.length === 0)) {
            setOptions(h ? [...h].reverse() : []);
        }
    }, []);

    useEffect(() => {
        setSearchType(props.searchType);
    }, [props.searchType]);

    useEffect(() => {
        let active = true;

        if (inputValue.length < 1) {
            // Display history in reverse order (most recent first) without mutating original state
            if (props.showHistory && history) {
                setOptions([...history].reverse());
            }
            return undefined;
        }
        else if (inputValue.length < ajaxOptions.minLength && ajaxOptions.enabled) {
            setOptions([]);
            return undefined;
        }

        if (ajaxOptions.url && ajaxOptions.enabled) {
            ajaxFetch(active, inputValue, searchType);
        }

        return () => {
            active = false;
        };
    }, [value, inputValue, isOpen, searchType, props.isFocused, history]);

    // Reset options visibility when open state changes
    useEffect(() => {
        if (isOpen) {
            setOptionsVisible(true);
            setPopperOpacity(1);
            if (props.setIsFocused) {
                props.setIsFocused(true);
            }
        }
        else {
            if (props.setIsFocused) {
                props.setIsFocused(false);
            }
        }
    }, [isOpen]);

    const trackingOptions = { // trackingOptions prop destructuring with defaults
        url: routes.SaveAction,
        enabled: true,
        save: (value) => {
            if (trackingOptions.enabled) {
                trackAutocompleteSearchAction(trackingOptions.url, value);
            }
        },
        ...props.trackingOptions
    };

    const ajaxOptions = { // ajaxOptions prop destructuring with defaults
        enabled: true,
        url: routes.ListingsAutocomplete,
        method: 'get',
        additionalData: null,
        dataKey: 'keywords',
        minLength: 3,
        onSearch: (active, searchValue, searchType) => {
            if (props.isFocused === false) return;

            if (searchType != 'Part') {
                setIsLoading(true);

                let url = ajaxOptions.url;
                if (props.IsCityStateZip) {
                    url = ajaxOptions.url + searchValue;
                }
                else if (ajaxOptions.method == 'get') {
                    url = buildUrl(url, ajaxOptions.dataKey, searchValue);

                    if (searchType == 'Attachment') {
                        url += `&SearchType=Attachment`;
                    } else if (searchType == 'Dismantled') {
                        url += `&Condition=Dismantled`;
                    }

                    if (ajaxOptions.additionalData) {
                        for (var key in ajaxOptions.additionalData) {
                            url += `&${key}=${ajaxOptions.additionalData[key]}`;
                        }
                    }
                }

                sandhillsRequest({
                    url: url,
                    method: ajaxOptions.method,
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((data) => {
                    if (active) {
                        let newOptions = [];

                        if (data) {
                            newOptions = [...newOptions, ...data];
                        }

                        setOptions(newOptions);
                        setIsLoading(false);
                    }
                });
            } else {
                setOptions([]);
            }
        },
        ...props.ajaxOptions
    };

    const searchOptions = { // searchOptions prop destructuring with defaults
        url: routes.Listings,
        text: translator.translate('sandhills.search'),
        additionalData: null,
        dataKey: 'keywords',
        isQuickFindCode: (v) => quickFindRegex.test(v),
        runQuickFindCheck: () => {
            var qfOption = options.find(o => o.TermType == 'QuickFind');
            if (qfOption) {
                window.location.href = qfOption.Url;
            } else {
                sandhillsRequest({
                    url: routes.GetQuickFindUrl,
                    data: {
                        quickFindCode: inputValue
                    },
                    errorHandling: {
                        useConsole: false,
                        useAlert: false
                    }
                }).then((data) => {
                    if (data) {
                        window.location.href = data;
                    } else {
                        searchOptions.defaultSearch();
                    }
                }).catch(() => {
                    searchOptions.defaultSearch();
                });
            }
        },
        defaultSearch: () => {
            var url = '';
            if (searchType == 'Part') {
                url = buildUrl(props.partsUrl, searchOptions.dataKey, inputValue, true);
            }
            else if (searchType == 'Attachment') {
                url = buildUrl(props.attachmentsUrl, searchOptions.dataKey, inputValue);
            }
            else if (searchType == 'Dismantled') {
                url = buildUrl(routes.Dismantled, searchOptions.dataKey, inputValue);
            }
            else {
                url = buildUrl(searchOptions.url, searchOptions.dataKey, inputValue);
            }
            if (searchOptions.additionalData) {
                for (var key in searchOptions.additionalData) {
                    url = buildUrl(url, key, searchOptions.additionalData[key]);
                }
            }
            if (props.showHistory) {
                setSearchHistoryCookie(inputValue, url);
            }

            window.location.href = url;
        },
        runSearch: () => {
            setOpen(false);
            trackingOptions.save(inputValue);

            //if inputValue is null or empty 
            if (!inputValue && props.SearchURLs) {
                window.location.href = getDefaultSearchURL(searchType);
            }
            else if (searchOptions.isQuickFindCode(inputValue)) {
                searchOptions.runQuickFindCheck();
            } else {
                searchOptions.defaultSearch();
            }
        },
        ...props.searchOptions
    };

    const getDefaultSearchURL = (key) => {
        switch (key) {
            case 'Equipment':
                return props.SearchURLs.DefaultEquipmentListPageUrl;
            case 'Part':
                return routes.Parts_Landing;
            case 'Attachment':
                return props.SearchURLs.DefaultAttachmentsListPageUrl;
            case 'Dismantled':
                return props.SearchURLs.DefaultDismantledListPageUrl;
            default:
                return props.SearchURLs.DefaultEquipmentListPageUrl;
        }
    };

    const endAdorn = (endAdornment) => {
        const newChildren = React.Children.map(endAdornment?.props?.children || [], c => c);

        if (!props.IsCityStateZip && !props.isQuickFind) {
            newChildren?.push(
                <SearchIconButton key={searchOptions.text} type='submit' onClick={(e) => { e.preventDefault(); e.stopPropagation(); searchOptions.runSearch(); }} aria-label={searchOptions.text} size={'small'}>
                    <SearchIcon fontSize={'small'} />
                </SearchIconButton>
            );
        }
        else if (props.isQuickFind) {
            newChildren?.push(
                //Search text button for quick find
                <QuickFindSearchButton key={searchOptions.text} type='submit' onClick={(e) => { e.preventDefault(); e.stopPropagation(); searchOptions.runSearch(); }} aria-label={searchOptions.text} size={'small'} sx={{ textTransform: 'none', fontSize: '12px' }}>
                    {windowSize.width < 500 ?
                        <SearchIcon fontSize={'small'} /> :
                        <strong style={{fontSize: '14px'}}>{translator.translate('sandhills.search')}</strong>
                    }
                </QuickFindSearchButton>
            );
        }

        const c = React.cloneElement(endAdornment ? endAdornment :
            <div
                style={{
                    position: 'absolute',
                    right: '9px',
                    top: 'calc(50% - 16px)'
                }}
            />,
            endAdornment?.props,
            newChildren);
        return c;
    };

    const buildUrl = useCallback((urlToBuild, dataKey, searchValue, isPart) => {
        let url = urlToBuild;

        if (isPart && !searchValue) {
            url = routes.Parts_Landing;
        }

        if (!url.endsWith('=') && searchValue) {
            url += url.includes('?') ? '&' : '?';
            url += `${dataKey}=`;
        }
        url += `${encodeURIComponent(searchValue)}`;

        if (props.isStickyHeader && !url.includes('sh=')) {
            url += `${url.includes('?') ? '&' : '?'}sh=1`;
        }

        return url;
    }, []);

    const ajaxFetch = useMemo(
        () => throttle((active, inputValue, searchType) => ajaxOptions.onSearch(active, inputValue, searchType), 200),
        [props.ajaxOptions, props.isFocused]
    );

    const getListBoxProps = () => {
        var lbProps = {
            style: {
                maxHeight: 500,
                border: '1px solid #ccc'
            }
        };
        return lbProps;
    };

    const onInputChange = (event, newInputValue) => {
        if (event?.key != 'Enter') {
            setInputValue(newInputValue);
        }
    };

    const onKeyDown = (e) => {
        if (e?.key === 'Enter' && props.autocompleteSearchField) {
            e.preventDefault();
            e.stopPropagation();
            searchOptions.runSearch();
        }
    };

    const handleInputOnClick = () => {
        if (props.setIsFocused) {
            props.setIsFocused(true);
            setOpen(true);
        }
    };

    const renderInput = (params) => {
        return (
            <TextField
                {...params}
                className='autocomplete-textfield'
                name={props.name}
                label={(props.enableVoiceToText && props.isListening) || hideLabel ? '' : props.label}
                placeholder={props.placeholder && !isOpen ? props.placeholder : ''}
                bgvariant={currentSite?.Site?.IsRVUniverse ? 'white' : null}
                hiddenLabel={props.hideLabel}
                onClick={handleInputOnClick}
                inputRef={ref}
                onChange={props.onTextChange}
                disabled={props.isListening}
                sx={props.sx}
                InputLabelProps={{ shrink: !props.isQuickFind && (isOpen || inputValue) }}
                InputProps={{
                ...params.InputProps,
                startAdornment: props.InputProps?.startAdornment ? props.InputProps.startAdornment :
                    (props.isQuickFind && windowSize.width >= 500) ? (
                        <InputAdornment position="start">
                            <IconButton
                                type='submit'
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); searchOptions.runSearch(); }}
                                aria-label={searchOptions.text}
                                size={'small'}
                            >
                                <SearchIcon fontSize={'small'} />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                endAdornment: useMemo(() => endAdorn(props.InputProps?.endAdornment ?? params.InputProps?.endAdornment), [isLoading, props.InputProps?.endAdornment, params.InputProps?.endAdornment, props.isListening, props.enableVoiceToText, searchType, inputValue]),
                style: {
                    ...props.textFieldStyles,
                    caretColor: props.isListening ? 'transparent' : 'inherit', // Hide cursor when listening
                    backgroundColor: props.styles?.bgVariant ? props.styles?.bgVariant : 'transparent',
                    borderRadius: props.isQuickFind ? '50px' : '4px 4px 0px 0px'
                },
                disableUnderline: props.disableUnderline ? true : false,
                readOnly: props.isListening, // Make input read-only when listening
                }}
            />
        );
    };

    const CustomPopper = (popperProps) => {
        // Store the reference to the popper element
        const { style, ...other } = popperProps;

        return (
            <Popper
                {...other}
                ref={popperRef}
                disablePortal={false}
                placement={'bottom-start'}
                style={{
                    ...style,
                    opacity: popperOpacity,
                    transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: 999999,
                }}
                transition
            />
        );
    };

    const onOpenHandler = () => {
        // Ensure options are visible and popper is opaque for animation
        setOptionsVisible(true);
        setPopperOpacity(1);
        setOpen(true);
    };

    const renderOption = (optionProps, option, { inputValue }) => {
        const matches = match(option.Label, inputValue);
        const parts = parse(option.Label, matches);
        if (!props.IsCityStateZip) {
            return (
                <Collapse className='autocomplete-popper' in={optionsVisible} appear={true} timeout={500} onClick={() => { gtmPushEvent(gtmEventType.keywordSelected) }}>
                    {props.showHistory && history && inputValue === '' && optionProps['data-option-index'] == 0 &&
                        <li>
                            <ListSubheader component="span" sx={{
                                display: 'block',
                                padding: '0',
                                fontSize: '12px',
                                fontWeight: 700,
                                fontStyle: 'normal',
                                lineHeight: '10px',
                                color: '#333'
                            }}>
                                {translator.translate('sandhills.tradesites.mvc.recentsearches')}
                            </ListSubheader>
                        </li>
                    }
                    {(history || inputValue.length > 0) &&
                        <li {...optionProps} style={{ width: '100%', display: 'block' }}>
                            <div style={{ paddingLeft: option?.Indent > 0 ? `${option.Indent * 10}px` : '' }}>
                                {parts.map((part, index) => (
                                    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                        {props.showHistory && inputValue === '' && <i className="fa-regular fa-clock media-buttons__icon"></i>}
                                        {part.text}
                                        {(index == parts.length - 1) && <i style={{ float: 'right' }} className="fa-solid fa-up-right-from-square"></i>}
                                    </span>
                                ))}
                            </div>
                        </li>
                    }
                </Collapse>
            );
        } else if (option?.isParent) {
            return (
                <li {...optionProps} {...(option.key ? { key: option.key } : {})}>
                    <b>{getOptionLabel(option)}</b>
                </li>
            );
        } else {
            return (
                <li {...optionProps} {...(option.key ? { key: option.key } : {})}>
                    {getOptionLabel(option)}
                </li>
            );
        }

    };

    return (
        <MuiAutocomplete
            className='autocomplete'
            filterOptions={(x) => x}
            freeSolo={props.IsCityStateZip ? false : true}
            fullWidth={true}
            popupIcon={null}
            getOptionLabel={getOptionLabel}
            id={props.id}
            ListboxProps={getListBoxProps()}
            loading={isLoading}
            loadingText={translator.translate('sandhills.loading')}
            onChange={onChange}
            onClose={(e, r) => {
                setOpen(false);
            }}
            onInputChange={onInputChange}
            onOpen={onOpenHandler}
            open={!props.isListening && (isOpen || props.isFocused)}
            options={options}
            openOnFocus={props.openOnFocus}
            renderInput={renderInput}
            renderOption={renderOption}
            size={'small'}
            value={props.isListening ? '' : props.value}  // Clear value during mic request too
            disableClearable={props.disableClearable}
            disabled={props.disabled}
            onKeyDown={onKeyDown}
            slots={{ popper: CustomPopper }}
            slotProps={{
                paper: {
                    elevation: 0
                }
            }}
        />
    );
});

export default Autocomplete;